import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Search, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

/** Administration of the referral programmes (permission "affiliation"):
 * members, balances, commissions, withdrawals, suspicious activity, status changes. */

import { nf } from "@/pages/Affiliate";

const PROGRAM = { ambassador: "Artiste Ambassadeur", influencer: "Fan Influenceur" };
const STATUS = { active: "Actif", disabled: "Désactivé", suspended: "Suspendu", blocked: "Bloqué" };
const STATUS_HELP = {
  active: "gagne des commissions, son code fonctionne, peut retirer",
  disabled: "ne gagne plus rien, code refusé, peut encore retirer son solde",
  suspended: "vérification en cours : ni commission ni retrait, solde gelé",
  blocked: "fraude : ni commission ni retrait, solde gelé",
};
const STATUS_COLOR = { active: "text-emerald-400", disabled: "text-zinc-400", suspended: "text-amber-400", blocked: "text-red-400" };

export default function AffiliatesAdmin() {
  const [ov, setOv] = useState(null);
  const [list, setList] = useState(null);
  const [sus, setSus] = useState([]);
  const [q, setQ] = useState("");
  const [program, setProgram] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);

  const loadList = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (program) p.set("program", program);
    if (status) p.set("status", status);
    api.get(`/admin/affiliates?${p}`).then((r) => setList(r.data)).catch(() => setList([]));
  };
  const loadAll = () => {
    api.get("/admin/affiliates/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/admin/affiliates/suspicious").then((r) => setSus(r.data)).catch(() => {});
    loadList();
  };
  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadList, [program, status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ov) return <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />;
  const usd = (n) => `${(n || 0).toFixed(2)} $`;
  const xaf = (n) => `${nf((n || 0) * ov.xaf_rate)} FCFA`;

  return (
    <div className="space-y-6" data-testid="admin-affiliates">
      <header>
        <h1 className="text-3xl font-black font-display">Affiliation</h1>
        <p className="text-sm text-zinc-400 mt-1">Commission de {String(ov.rate_purchase_pct).replace(".", ",")} % de la part nette de Metazik sur chaque achat ou vente d'un affilié, pendant 12 mois · retrait dès {nf(ov.min_withdrawal_xaf)} FCFA</p>
      </header>

      <FeeSetting fee={ov.fee} onSaved={loadAll} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Membres Artistes Ambassadeurs" value={ov.members.ambassador} />
        <Card label="Membres Fans Influenceurs" value={ov.members.influencer} />
        <Card label="Comptes affiliés (inscrits avec un code promo)" value={ov.referred_accounts} />
        <Card label="Commissions gagnées" value={xaf(ov.totals_usd.lifetime_earned)} sub={usd(ov.totals_usd.lifetime_earned)} />
        <Card label="Soldes disponibles" value={xaf(ov.totals_usd.available)} sub={usd(ov.totals_usd.available)} />
        <Card label="Retraits en cours" value={xaf(ov.totals_usd.pending_payout)} />
        <Card label="Déjà retiré" value={xaf(ov.totals_usd.lifetime_withdrawn)} />
        <Card label="Reversé à Metazik (comptes supprimés)" value={xaf(ov.totals_usd.forfeited)} />
      </div>

      {sus.length > 0 && (
        <section className="rounded-2xl bg-amber-500/5 border border-amber-500/20 p-5" data-testid="affiliates-suspicious">
          <h3 className="text-sm font-black uppercase tracking-wider text-amber-300 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Activités à vérifier</h3>
          <div className="space-y-2">
            {sus.map((s) => (
              <button key={s.user.id} onClick={() => setOpenId(s.user.id)} className="w-full text-left rounded-xl bg-white/5 px-3 py-2 hover:bg-white/10">
                <p className="text-sm font-bold text-white">{s.user.name} <span className="text-zinc-500 font-normal">· {s.user.referral_code}</span></p>
                {s.signals.map((x) => <p key={x} className="text-xs text-amber-200">• {x}</p>)}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          <form onSubmit={(e) => { e.preventDefault(); loadList(); }} className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail ou code" className="input-base pl-9 text-sm" />
          </form>
          <select value={program} onChange={(e) => setProgram(e.target.value)} className="input-base text-sm w-auto">
            <option value="">Tous les programmes</option>
            <option value="ambassador">Artistes ambassadeurs</option>
            <option value="influencer">Fans influenceurs</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-base text-sm w-auto">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {list === null ? <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" /> : list.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-6">Aucun membre</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500 uppercase tracking-wider text-[10px] font-black">
                {["Membre", "Programme", "Code promo", "Statut", "Affiliés", "Gagné", "Disponible"].map((h) => <th key={h} className="text-left py-2 pr-4">{h}</th>)}
              </tr></thead>
              <tbody>{list.map((a) => (
                <tr key={a.id} onClick={() => setOpenId(a.id)} className="border-t border-white/5 cursor-pointer hover:bg-white/5" data-testid={`affiliate-row-${a.id}`}>
                  <td className="py-2 pr-4 text-white font-semibold">{a.name}<div className="text-zinc-500 font-normal">{a.email}</div></td>
                  <td className="py-2 pr-4 text-zinc-300">{PROGRAM[a.affiliate_program]}</td>
                  <td className="py-2 pr-4 text-zinc-300 tracking-widest">{a.referral_code}</td>
                  <td className={`py-2 pr-4 font-bold ${STATUS_COLOR[a.affiliate_status]}`}>{STATUS[a.affiliate_status]}</td>
                  <td className="py-2 pr-4 text-zinc-300">{a.referred}</td>
                  <td className="py-2 pr-4 text-zinc-300">{xaf(a.lifetime_earned)}</td>
                  <td className="py-2 pr-4 text-zinc-300">{xaf(a.available)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      {openId && <AffiliateDetail id={openId} xaf={xaf} usd={usd} onClose={() => setOpenId(null)} onChanged={loadAll} />}
    </div>
  );
}

/** What the payment gateway keeps on each payment (% of the price): taken off Metazik's
 * 30 % before the commission is computed. */
function FeeSetting({ fee, onSaved }) {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";   // money settings: the owner only
  const value = fee?.gateway_fee_pct;
  const edit = async () => {
    const raw = window.prompt("Frais MyPVit payés par Metazik quand un artiste retire l'argent de ses ventes, en % du montant retiré.\nNormalement relevés automatiquement sur MyPVit : ne remplis ce champ que pour les corriger. Laisse vide pour revenir au taux relevé automatiquement.", value ?? "");
    if (raw === null) return;
    const pct = String(raw).trim() === "" ? null : parseFloat(String(raw).replace(",", "."));
    if (pct !== null && !(pct >= 0 && pct <= 30)) { toast.error("Donne un pourcentage entre 0 et 30."); return; }
    try {
      await api.put("/admin/affiliates/settings", { gateway_fee_pct: pct });
      toast.success("Frais enregistrés.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const missing = value === null || value === undefined;
  return (
    <section className={`rounded-2xl p-4 border flex items-center gap-3 flex-wrap ${missing ? "bg-amber-500/10 border-amber-500/30" : "bg-white/[0.03] border-white/10"}`} data-testid="affiliate-fee-setting">
      <div className="flex-1 min-w-[220px]">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Frais MyPVit payés par Metazik au retrait des artistes</p>
        <p className={`text-xl font-black mt-1 ${missing ? "text-amber-200" : "text-white"}`}>{missing ? "En attente du premier retrait d'artiste" : `${String(value).replace(".", ",")} % du montant retiré`}</p>
        <p className="text-[11px] text-zinc-500 mt-1">{fee?.source === "manual" ? "Taux saisi à la main." : fee?.source === "mypvit" ? `Relevé automatiquement sur MyPVit (dernier retrait d'artiste : ${new Date(fee.observed_at).toLocaleDateString("fr-FR")}).` : "Il sera relevé automatiquement sur MyPVit dès le premier retrait d'un artiste."} Ces frais sont retirés des 30 % de Metazik avant le calcul de la commission. Les retraits de commissions, eux, sont payés par celui qui retire.</p>
      </div>
      {isOwner && <button onClick={edit} className="btn-secondary text-xs" data-testid="affiliate-fee-edit">Modifier</button>}
    </section>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</p>
      <p className="text-xl font-black text-white mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-zinc-500">{sub}</p>}
    </div>
  );
}

function AffiliateDetail({ id, xaf, usd, onClose, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";   // cancelling a commission: the owner only
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/affiliates/${id}`).then((r) => setD(r.data)).catch(() => { toast.error("Introuvable"); onClose(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (status) => {
    const reason = window.prompt(`Nouveau statut : ${STATUS[status]} (${STATUS_HELP[status]}).\nRaison (obligatoire, gardée dans l'historique) :`);
    if (!reason || reason.trim().length < 5) { if (reason !== null) toast.error("Donne une raison d'au moins 5 caractères."); return; }
    try {
      await api.patch(`/admin/affiliates/${id}`, { status, reason: reason.trim() });
      toast.success("Statut mis à jour, l'affilié est prévenu.");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (c) => {
    const reason = window.prompt(`Annuler cette commission de ${usd(c.amount_usd)} (vente remboursée, fraude…) ? Elle sera retirée de son solde.\nRaison :`);
    if (!reason || reason.trim().length < 5) { if (reason !== null) toast.error("Donne une raison d'au moins 5 caractères."); return; }
    try {
      await api.post(`/admin/affiliates/commissions/${c.id}/cancel`, { reason: reason.trim() });
      toast.success("Commission annulée.");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="affiliate-detail">
      <div className="w-full max-w-3xl max-h-[90dvh] overflow-y-auto bg-zinc-950 border border-white/10 rounded-3xl p-6 relative space-y-5">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 text-zinc-400" aria-label="close"><X className="w-4 h-4" /></button>
        {!d ? <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" /> : (
          <>
            <div>
              <h2 className="text-2xl font-black font-display text-white pr-10">{d.user.name}</h2>
              <p className="text-xs text-zinc-400">{d.user.email} · {PROGRAM[d.user.affiliate_program]} · code <span className="tracking-widest text-white">{d.user.referral_code}</span></p>
              <p className={`text-sm font-bold mt-1 ${STATUS_COLOR[d.user.affiliate_status]}`}>{STATUS[d.user.affiliate_status]} <span className="text-zinc-500 font-normal">— {STATUS_HELP[d.user.affiliate_status]}</span></p>
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.keys(STATUS).filter((s) => s !== d.user.affiliate_status).map((s) => (
                  <button key={s} onClick={() => setStatus(s)} className="btn-secondary text-xs" data-testid={`affiliate-set-${s}`}>
                    {{ active: "Activer", disabled: "Désactiver", suspended: "Suspendre", blocked: "Bloquer" }[s]}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Card label="Total gagné" value={xaf(d.balance.lifetime_earned)} />
              <Card label="Disponible" value={xaf(d.balance.available)} />
              <Card label="Retrait en cours" value={xaf(d.balance.pending_payout)} />
              <Card label="Déjà retiré" value={xaf(d.balance.lifetime_withdrawn)} />
            </div>
            <Block title={`Commissions (${d.commissions.length})`}>
              {d.commissions.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-xs border-t border-white/5 py-1.5">
                  <span className="text-zinc-500 w-20 shrink-0">{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                  <span className="text-zinc-300 flex-1 min-w-0 truncate">{c.source === "sale" ? "Vente de" : "Achat de"} {c.referred_user_name} · {c.kind === "release" ? "projet" : "chanson"}</span>
                  <span className={`tabular-nums ${c.status === "cancelled" ? "line-through text-zinc-600" : "text-white"}`}>{usd(c.amount_usd)}</span>
                  {c.status === "earned" ? (isOwner && <button onClick={() => cancel(c)} className="text-red-400 hover:text-red-300 underline">annuler</button>) : <span className="text-zinc-600">annulée</span>}
                </div>
              ))}
            </Block>
            <Block title={`Ses affiliés (${d.referred.length})`}>
              {d.referred.map((u) => (
                <div key={u.id} className="flex gap-2 text-xs border-t border-white/5 py-1.5">
                  <span className="text-white flex-1">{u.name}</span><span className="text-zinc-400">{u.role === "artist" ? "Artiste" : "Fan"}</span>
                  <span className="text-zinc-500">{new Date(u.created_at).toLocaleDateString("fr-FR")}</span>
                </div>
              ))}
            </Block>
            <Block title={`Retraits (${d.payouts.length})`}>
              {d.payouts.map((p) => (
                <div key={p.id} className="flex gap-2 text-xs border-t border-white/5 py-1.5">
                  <span className="text-zinc-500">{new Date(p.requested_at).toLocaleString("fr-FR")}</span>
                  <span className="text-white flex-1">{nf(p.payout_amount || 0)} {p.payout_currency}</span>
                  <span className="text-zinc-400">{p.status}</span>
                </div>
              ))}
            </Block>
            <Block title="Historique des actions">
              {d.history.map((h) => (
                <div key={h.id} className="text-xs border-t border-white/5 py-1.5 text-zinc-300">
                  <span className="text-zinc-500">{new Date(h.created_at).toLocaleString("fr-FR")}</span> · {h.action.replace("affiliate_", "")} · {h.detail?.reason}
                </div>
              ))}
            </Block>
          </>
        )}
      </div>
    </div>
  );
}

function Block({ title, children }) {
  const empty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <section>
      <h3 className="text-xs font-black uppercase tracking-wider text-zinc-400 mb-1">{title}</h3>
      {empty ? <p className="text-xs text-zinc-600 py-2">Rien pour l'instant</p> : children}
    </section>
  );
}
